/**
 * Listado de órdenes de trabajo.
 *
 * La bandeja de la oficina. La columna que importa no es el estado sino la
 * **fecha límite legal**: pasarla incumple el art. 21.1, y una OT cerrada
 * fuera de plazo ya no se puede salvar.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { DataTable, type Column } from '../table/data-table.js';
import { decodeTableState, encodeTableState, type TableState } from '../table/table-state.js';

interface WorkOrderRow {
  id: string;
  number: string;
  status: string;
  priority: string;
  work_order_type: string;
  customer_name: string;
  site_name: string;
  technician_name: string | null;
  scheduled_date: string | null;
  legal_deadline: string | null;
  days_to_deadline: number | null;
}

interface WorkOrderPage {
  items: WorkOrderRow[];
  nextCursor: string | null;
  total: number | null;
}

/** Los catorce estados de la migración 0011, agrupados por lo que significan. */
const STATUS: Record<string, { label: string; tone: string }> = {
  PENDIENTE: { label: 'Pendiente', tone: 'off' },
  PROGRAMADA: { label: 'Programada', tone: 'info' },
  ASIGNADA: { label: 'Asignada', tone: 'info' },
  DESPLAZAMIENTO: { label: 'En desplazamiento', tone: 'info' },
  INICIADA: { label: 'Iniciada', tone: 'info' },
  PAUSADA: { label: 'Pausada', tone: 'warn' },
  PARCIAL: { label: 'Parcial', tone: 'warn' },
  TERMINADA: { label: 'Terminada', tone: 'ok' },
  PENDIENTE_FIRMA: { label: 'Pendiente de firma', tone: 'warn' },
  PENDIENTE_MATERIAL: { label: 'Pendiente de material', tone: 'warn' },
  PENDIENTE_PRESUPUESTO: { label: 'Pendiente de presupuesto', tone: 'warn' },
  CERRADA: { label: 'Cerrada', tone: 'ok' },
  FACTURADA: { label: 'Facturada', tone: 'ok' },
  ANULADA: { label: 'Anulada', tone: 'off' },
  UNKNOWN: { label: 'Sin clasificar', tone: 'off' },
};

const PRIORITY: Record<string, { label: string; tone: string; icon: string }> = {
  URGENTE: { label: 'Urgente', tone: 'bad', icon: '▲' },
  ALTA: { label: 'Alta', tone: 'warn', icon: '▲' },
  MEDIA: { label: 'Media', tone: 'off', icon: '■' },
  BAJA: { label: 'Baja', tone: 'off', icon: '▼' },
};

/** Estados en los que la OT ya no puede salvar el plazo. */
const CLOSED = new Set(['CERRADA', 'FACTURADA', 'ANULADA']);

function deadlineCell(row: WorkOrderRow): React.ReactNode {
  if (row.legal_deadline === null) return <span className="due due--none">—</span>;

  const days = row.days_to_deadline ?? 0;

  // Una OT cerrada fuera de plazo incumplió, pero ya no hay nada que hacer:
  // pintarla en rojo en la bandeja de trabajo llenaría la pantalla de
  // alarmas sobre las que no se puede actuar.
  if (CLOSED.has(row.status)) {
    return <span className="due">{row.legal_deadline}</span>;
  }

  if (days < 0) {
    return (
      <span className="due due--late">
        ⚠ Fuera de plazo {Math.abs(days)} d · {row.legal_deadline}
      </span>
    );
  }
  if (days <= 7) {
    return (
      <span className="due due--soon">
        ◷ {days} d · {row.legal_deadline}
      </span>
    );
  }
  return <span className="due">{row.legal_deadline}</span>;
}

const COLUMNS: Column<WorkOrderRow>[] = [
  {
    id: 'number',
    label: 'Nº',
    width: '10rem',
    render: (r) => <Link to={`/ordenes/${r.id}`}>{r.number}</Link>,
    exportValue: (r) => r.number,
  },
  {
    id: 'priority',
    label: 'Prioridad',
    width: '7rem',
    render: (r) => {
      const p = PRIORITY[r.priority] ?? PRIORITY['MEDIA']!;
      return <span className={`pill pill--${p.tone}`}>{`${p.icon} ${p.label}`}</span>;
    },
    exportValue: (r) => (PRIORITY[r.priority] ?? PRIORITY['MEDIA']!).label,
  },
  { id: 'type', label: 'Tipo', render: (r) => r.work_order_type, width: '8rem' },
  { id: 'customerName', label: 'Cliente', render: (r) => r.customer_name },
  { id: 'siteName', label: 'Centro', render: (r) => r.site_name },
  {
    id: 'technicianName',
    label: 'Técnico',
    render: (r) => r.technician_name ?? <span className="due--none">Sin asignar</span>,
    exportValue: (r) => r.technician_name ?? 'Sin asignar',
  },
  {
    id: 'status',
    label: 'Estado',
    width: '12rem',
    render: (r) => {
      const s = STATUS[r.status] ?? STATUS['UNKNOWN']!;
      return <span className={`pill pill--${s.tone}`}>{s.label}</span>;
    },
    exportValue: (r) => (STATUS[r.status] ?? STATUS['UNKNOWN']!).label,
  },
  { id: 'scheduledDate', label: 'Programada', render: (r) => r.scheduled_date ?? '—', width: '8rem' },
  {
    id: 'legalDeadline',
    label: 'Fecha límite legal',
    width: '14rem',
    render: deadlineCell,
    exportValue: (r) => r.legal_deadline ?? '',
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'open', label: 'Abiertas' },
  { id: 'breached', label: 'Fuera de plazo' },
  { id: 'all', label: 'Todas' },
] as const;

export function WorkOrdersScreen(): React.ReactNode {
  const { api } = useSession();
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => decodeTableState(params, COLUMN_IDS), [params]);
  const quick = params.get('v') ?? 'open';

  const [rows, setRows] = useState<WorkOrderRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const request = useRef(0);

  const load = useCallback(
    async (from: string | null, append: boolean) => {
      const ticket = ++request.current;
      setLoading(true);

      const query = new URLSearchParams();
      if (state.search.trim()) query.set('search', state.search.trim());
      const priority = state.filters['priority'];
      if (priority?.operator === 'eq' && priority.values[0]) query.set('priority', priority.values[0]);
      if (quick === 'open') query.set('openOnly', 'true');
      if (quick === 'breached') query.set('breached', 'true');
      if (from) query.set('cursor', from);
      query.set('limit', '100');

      try {
        const page = await api.get<WorkOrderPage>(`/work-orders?${query.toString()}`);
        if (ticket !== request.current) return;

        setRows((previous) => (append ? [...previous, ...page.items] : page.items));
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
        if (!append) setTotal(page.total);
        setError(null);
      } catch (caught) {
        if (ticket !== request.current) return;
        setError(caught instanceof ApiError ? caught.message : 'No se han podido cargar las órdenes.');
      } finally {
        if (ticket === request.current) setLoading(false);
      }
    },
    [api, state.search, state.filters, quick],
  );

  useEffect(() => {
    const timer = setTimeout(() => void load(null, false), 250);
    return () => clearTimeout(timer);
  }, [load]);

  const onStateChange = useCallback(
    (next: TableState) => {
      const encoded = encodeTableState(next);
      if (quick !== 'open') encoded.set('v', quick);
      setParams(encoded, { replace: true });
    },
    [setParams, quick],
  );

  function setQuick(id: string): void {
    const encoded = encodeTableState(state);
    if (id !== 'open') encoded.set('v', id);
    setParams(encoded, { replace: true });
  }

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Órdenes de trabajo</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick === option.id}
              onClick={() => setQuick(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <Link className="button button--primary button--small" to="/ordenes/nueva">
          Nueva orden
        </Link>
      </header>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={rows}
        idOf={(r) => r.id}
        state={state}
        onStateChange={onStateChange}
        exportName={quick === 'breached' ? 'ot-fuera-de-plazo' : 'ordenes-de-trabajo'}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={() => void load(cursor, true)}
        totalMatching={total}
        emptyMessage={
          quick === 'breached'
            ? 'Ninguna orden abierta fuera de plazo.'
            : 'No hay órdenes que coincidan.'
        }
      />
    </section>
  );
}
